import type { BlockRecord } from '../blocks/types';
import type { BlockPropertyRecord } from './types';
import { parseDatePayload } from '@couplespace/core/databases';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

export function parseDateIsoFromPayload(value: unknown): string | null {
  if (!value || typeof value !== 'object' || !('t' in value)) {
    return null;
  }
  const raw = (value as { v?: unknown }).v;
  if (typeof raw !== 'string' || !raw.trim()) {
    return null;
  }
  const match = raw.trim().match(ISO_DATE);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

export function rowDateRange(
  row: BlockRecord,
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
  columnId: string,
): { start: string | null; end: string | null } {
  const prop = (propertiesByRowId[row.id] ?? []).find((p) => p.propertyId === columnId);
  return parseDatePayload(prop?.value ?? null);
}

export function rowDateIso(
  row: BlockRecord,
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
  columnId: string,
): string | null {
  return rowDateRange(row, propertiesByRowId, columnId).start;
}

export type MonthCell = {
  day: number | null;
  iso: string | null;
};

/** Monday-first month grid including leading/trailing padding cells. */
export function buildMonthGrid(year: number, month: number): MonthCell[] {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mondayIndex = (first.getDay() + 6) % 7;
  const cells: MonthCell[] = [];
  for (let i = 0; i < mondayIndex; i++) {
    cells.push({ day: null, iso: null });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    cells.push({ day, iso });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ day: null, iso: null });
  }
  return cells;
}

export function bucketRowsByDateIso(
  rows: BlockRecord[],
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
  columnId: string,
): { dated: Map<string, BlockRecord[]>; undated: BlockRecord[] } {
  const dated = new Map<string, BlockRecord[]>();
  const undated: BlockRecord[] = [];
  for (const row of rows) {
    const { start, end } = rowDateRange(row, propertiesByRowId, columnId);
    if (!start) {
      undated.push(row);
      continue;
    }
    const rangeEnd = end ?? start;
    const [rangeStart, rangeEndNorm] =
      rangeEnd < start ? [rangeEnd, start] : [start, rangeEnd];
    let iso = rangeStart;
    while (iso <= rangeEndNorm) {
      const list = dated.get(iso);
      if (list) {
        list.push(row);
      } else {
        dated.set(iso, [row]);
      }
      if (iso === rangeEndNorm) {
        break;
      }
      iso = nextIsoDate(iso);
    }
  }
  return { dated, undated };
}

function nextIsoDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function rowsForMonth(
  rows: BlockRecord[],
  propertiesByRowId: Record<string, BlockPropertyRecord[]>,
  columnId: string,
  year: number,
  month: number,
): { byDay: Map<number, BlockRecord[]>; undated: BlockRecord[] } {
  const prefix = `${year}-${String(month + 1).padStart(2, '0')}-`;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const byDay = new Map<number, BlockRecord[]>();
  const undated: BlockRecord[] = [];

  for (const row of rows) {
    const { start, end } = rowDateRange(row, propertiesByRowId, columnId);
    if (!start) {
      undated.push(row);
      continue;
    }
    const rangeEnd = end ?? start;
    const [rangeStart, rangeEndNorm] =
      rangeEnd < start ? [rangeEnd, start] : [start, rangeEnd];

    for (let day = 1; day <= daysInMonth; day++) {
      const iso = `${prefix}${String(day).padStart(2, '0')}`;
      if (iso < rangeStart || iso > rangeEndNorm) {
        continue;
      }
      const list = byDay.get(day);
      if (list) {
        list.push(row);
      } else {
        byDay.set(day, [row]);
      }
    }
  }
  return { byDay, undated };
}

export function monthLabel(year: number, month: number): string {
  return new Date(year, month, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const d = new Date(year, month + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() };
}
